import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import Logo from '@/components/Logo'
import { useLang } from '@/hooks/useLang'
import { POPULAR_COUNTRY_CODES, getCountryConfig } from '@/config/countries'
import { countryToSlug } from '@/lib/countries'
import { Flag } from '@/components/Flag'
const TOP_COUNTRIES = POPULAR_COUNTRY_CODES.slice(0, 6).map(code => getCountryConfig(code)!)

export default function Footer() {
  const { t } = useTranslation('common')
  const lang = useLang()
  const year = new Date().getFullYear()

  return (
    <footer className="bg-muted/30 border-t">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="mb-4">
              <Logo />
            </div>
            <p className="text-muted-foreground text-sm">
              {t('footer.description')}
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-base">{t('footer.product')}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><a href="/#features" className="hover:text-foreground transition-colors block py-1">{t('navigation.features')}</a></li>
              <li><Link to="/$lang/pricing" params={{ lang }} className="hover:text-foreground transition-colors block py-1">{t('navigation.pricing')}</Link></li>
              <li><Link to="/$lang/openclaw" params={{ lang }} className="hover:text-foreground transition-colors block py-1">{t('navigation.agentApi')}</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-base">{t('footer.support')}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/$lang/docs/" params={{ lang }} className="hover:text-foreground transition-colors block py-1">{t('navigation.docs')}</Link></li>
              <li><Link to="/$lang/faq" params={{ lang }} className="hover:text-foreground transition-colors block py-1">{t('navigation.faq')}</Link></li>
              <li><Link to="/blog" className="hover:text-foreground transition-colors block py-1">{t('navigation.blog')}</Link></li>
              <li><Link to="/$lang/tools" params={{ lang }} className="hover:text-foreground transition-colors block py-1">{t('navigation.tools')}</Link></li>
            </ul>
          </div>

          {/* Top Countries */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-base">{t('footer.topCountries')}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {TOP_COUNTRIES.map((c) => (
                <li key={c.code}>
                  <Link
                    to="/$lang/rates/$country"
                    params={{ lang, country: countryToSlug(c.code) }}
                    className="hover:text-foreground transition-colors block py-1"
                  >
                    <Flag countryCode={c.code} size="sm" /> {t('footer.howToCall', { country: t(`countries.${c.code}`) })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-foreground mb-4 text-base">{t('footer.legal')}</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground transition-colors block py-1">{t('navigation.privacy')}</Link></li>
              <li><Link to="/terms" className="hover:text-foreground transition-colors block py-1">{t('navigation.terms')}</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-muted-foreground text-center md:text-left">
            {t('footer.copyright', { year })}
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher size="sm" />
            <ThemeToggle />
          </div>
        </div>
      </div>
    </footer>
  )
}
